<template>
  <div class="mb-2" :class="[showDeleteButton && 'package-delete-row']">
    <!-- Paket Başlığı -->
    <b-row v-if="showHeader">
      <b-col cols="12" class="mb-1">
        <h4>{{ t('Package') }}: {{ index }}</h4>
      </b-col>
    </b-row>

    <!-- Ölçü Alanları + Post Type + Tehlikeli Madde (üst borderlı kutu) -->
    <div class="package-section mb-1">
      <b-row>
        <!-- Post Type -->
        <b-col v-if="showPostType" md="6" lg="3">
          <validation-provider
            #default="{ errors }"
            ref="post_type"
            :name="t('Post Type')"
            rules="required"
          >
            <b-form-group label-for="post_type">
              <template #label>
                <span>{{ t('Post Type') }}</span>
                <span v-if="validationAttempted && errors.length" class="required-asterisk">*</span>
              </template>
              <b-form-select
                v-model="values.post_type"
                :class="[errors[0] && 'is-invalid']"
                :options="consignmentPostTypeOptions"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Ölçü Alanları -->
        <b-col md="6" lg="3">
          <validation-provider
            #default="{ errors }"
            ref="weight"
            :name="t('Weight (kg)')"
            rules="required|doubleOrInteger"
          >
            <b-form-group label-for="weight">
              <template #label>
                <span>{{ t('Weight (kg)') }}</span>
                <span v-if="validationAttempted && errors.length" class="required-asterisk">*</span>
              </template>
              <b-form-input
                id="weight"
                v-model="values.weight"
                name="weight"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Weight (kg)')"
                autocomplete="off"
                type="number"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="6" lg="3">
          <validation-provider
            #default="{ errors }"
            ref="length"
            :name="t('Length')"
            rules="required|integer"
          >
            <b-form-group label-for="length">
              <template #label>
                <span>{{ t('Length') }}</span>
                <span v-if="validationAttempted && errors.length" class="required-asterisk">*</span>
              </template>
              <b-form-input
                id="length"
                v-model="values.length"
                name="length"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Length')"
                autocomplete="off"
                type="number"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="6" lg="3">
          <validation-provider
            #default="{ errors }"
            ref="width"
            :name="t('Width')"
            rules="required|integer"
          >
            <b-form-group label-for="width">
              <template #label>
                <span>{{ t('Width') }}</span>
                <span v-if="validationAttempted && errors.length" class="required-asterisk">*</span>
              </template>
              <b-form-input
                id="width"
                v-model="values.width"
                name="width"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Width')"
                autocomplete="off"
                type="number"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="6" lg="3">
          <validation-provider
            #default="{ errors }"
            ref="height"
            :name="t('Height')"
            rules="required|integer"
          >
            <b-form-group label-for="height">
              <template #label>
                <span>{{ t('Height') }}</span>
                <span v-if="validationAttempted && errors.length" class="required-asterisk">*</span>
              </template>
              <b-form-input
                id="height"
                v-model="values.height"
                name="height"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Height')"
                autocomplete="off"
                type="number"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Tehlikeli Madde -->
        <b-col v-if="showDangerousGoods" md="6" lg="3">
          <b-form-group label-for="dangerous_goods">
            <validation-provider ref="dangerous_goods">
              <div class="d-flex flex-column">
                <label class="mb-50">
                  {{ $t('Does it contain dangerous substances?') }}
                  <span v-if="validationAttempted && dangerousGoodsError" class="required-asterisk"
                    >*</span
                  >
                </label>
                <b-form-select v-model="values.dangerous_goods" :options="dangerousGoodsOptions" />
                <small v-if="dangerousGoodsError" class="text-danger">
                  {{ dangerousGoodsError }}
                </small>
              </div>
            </validation-provider>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Ürün İçeriği Alanı -->
    <div class="product-section mt-1">
      <div class="product-section__header">
        <div class="product-section__bookmark d-flex align-items-center justify-content-center">
          <feather-icon icon="BookmarkIcon" size="16" />
        </div>

        <div class="flex-grow-1 mr-1">
          <b-form-group class="mb-0">
            <v-select
              class="saved-products-select"
              v-model="selectedSavedProduct"
              :options="savedProductOptions"
              label="text"
              :reduce="option => option.value"
              :clearable="false"
              :placeholder="t('Fill From Saved Products')"
              @input="handleSavedProductChange"
            >
              <span slot="no-options">{{ t('No records found') }}</span>
            </v-select>
          </b-form-group>
        </div>

        <b-button
          variant="primary"
          size="sm"
          class="product-section__add-btn"
          @click="addProductRow"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          {{ t('Add New Product') }}
        </b-button>
      </div>

      <div v-for="(product, idx) in products" :key="idx" class="product-section__row">
        <b-row class="d-flex align-items-center">
          <b-col cols="12" md="5" lg="5">
            <b-form-group :label-for="`product_desc_${index}_${idx}`">
              <template #label>
                <span>{{ `${t('Product Name')}${isDomestic ? ` (${t('Optional')})` : ''}` }}</span>
                <span
                  v-if="
                    validationAttempted &&
                    productErrors[idx] &&
                    productErrors[idx].description &&
                    !isDomestic
                  "
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <div class="d-flex flex-column flex-md-row align-items-md-center">
                <b-form-input
                  :id="`product_desc_${index}_${idx}`"
                  v-model="product.description"
                  :placeholder="t('Product Name')"
                  class="flex-grow-1"
                  :class="[
                    validationAttempted && productErrors[idx] && productErrors[idx].description
                      ? 'is-invalid'
                      : ''
                  ]"
                  @input="handleDescriptionInput(idx, $event)"
                />
                <b-button
                  v-if="isInternational"
                  variant="primary"
                  size="sm"
                  class="mt-1 mt-md-0 ml-md-1"
                  type="button"
                  @click="translateProductName(idx)"
                >
                  {{ t('Translate to English') }}
                </b-button>
              </div>
            </b-form-group>
          </b-col>

          <!-- Para Birimi -->
          <b-col cols="12" md="3" lg="3">
            <b-form-group :label-for="`product_currency_${index}_${idx}`">
              <template #label>
                <span>{{ `${t('Currency Code')}${isDomestic ? ` (${t('Optional')})` : ''}` }}</span>
                <span
                  v-if="
                    validationAttempted &&
                    productErrors[idx] &&
                    productErrors[idx].currency &&
                    !isDomestic
                  "
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <v-select
                :id="`product_currency_${index}_${idx}`"
                v-model="product.currency"
                :options="currencies"
                :placeholder="t('Make a Choice')"
                label="text"
                :class="[
                  validationAttempted && productErrors[idx] && productErrors[idx].currency
                    ? 'is-invalid'
                    : ''
                ]"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- Adet -->
          <b-col cols="12" md="2" lg="2" class="product-field product-field--qty">
            <b-form-group :label-for="`product_quantity_${index}_${idx}`">
              <template #label>
                <span>{{ `${t('Quantity')}${isDomestic ? ` (${t('Optional')})` : ''}` }}</span>
                <span
                  v-if="
                    validationAttempted &&
                    productErrors[idx] &&
                    productErrors[idx].quantity &&
                    !isDomestic
                  "
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <b-form-input
                :id="`product_quantity_${index}_${idx}`"
                v-model="product.quantity"
                :placeholder="t('Quantity')"
                type="number"
                autocomplete="off"
                :class="[
                  validationAttempted && productErrors[idx] && productErrors[idx].quantity
                    ? 'is-invalid'
                    : ''
                ]"
              />
            </b-form-group>
          </b-col>

          <!-- Birim Fiyatı -->
          <b-col cols="3" md="auto" class="product-field product-field--price">
            <b-form-group :label-for="`product_price_${index}_${idx}`">
              <template #label>
                <span>{{ `${t('Unit Price')}${isDomestic ? ` (${t('Optional')})` : ''}` }}</span>
                <span
                  v-if="
                    validationAttempted &&
                    productErrors[idx] &&
                    productErrors[idx].unit_price &&
                    !isDomestic
                  "
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <b-form-input
                :id="`product_price_${index}_${idx}`"
                v-model="product.unit_price"
                :placeholder="t('Unit Price')"
                type="number"
                step="0.0001"
                autocomplete="off"
                :class="[
                  validationAttempted && productErrors[idx] && productErrors[idx].unit_price
                    ? 'is-invalid'
                    : ''
                ]"
              />
            </b-form-group>
          </b-col>

          <!-- Satır Sil -->
          <b-col class="d-flex align-items-end justify-content-center">
            <b-button
              variant="outline-danger"
              type="button"
              class="icon-button"
              @click="removeProductRow(idx)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </b-col>
        </b-row>

        <!-- Satır 2: Yurt dışı ise GTIP + Menşei -->
        <b-row v-if="isInternational" class="mt-1">
          <!-- GTIP Kodu -->
          <b-col cols="12" md="6" class="mb-1">
            <b-form-group :label-for="`gtip_${index}_${idx}`">
              <template #label>
                <span>{{
                  `${t('Gtip Code')}${product && product.gtip ? ` (${product.gtip})` : ''}`
                }}</span>
                <span
                  v-if="validationAttempted && productErrors[idx] && productErrors[idx].gtip"
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <div
                :class="[
                  validationAttempted && productErrors[idx] && productErrors[idx].gtip
                    ? 'is-invalid'
                    : ''
                ]"
              >
                <SelectBox
                  :id="`gtip_${index}_${idx}`"
                  :ref="`gtipSelect_${index}_${idx}`"
                  name="gtip"
                  url="api/v1/gtip/search"
                  :value="product.gtipSelect"
                  returned-key="code"
                  :min="4"
                  @setValue="setGtipValue(idx, $event)"
                />
              </div>
              <input v-model="product.gtip" type="hidden" />
            </b-form-group>
          </b-col>

          <!-- Menşei -->
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group :label-for="`origin_${index}_${idx}`">
              <template #label>
                <span>{{ t('Origin') }}</span>
                <span
                  v-if="validationAttempted && productErrors[idx] && productErrors[idx].origin"
                  class="required-asterisk"
                  >*</span
                >
              </template>
              <v-select
                :id="`origin_${index}_${idx}`"
                v-model="product.origin"
                :options="countries"
                :placeholder="t('Make a Choice')"
                label="text"
                :class="[
                  validationAttempted && productErrors[idx] && productErrors[idx].origin
                    ? 'is-invalid'
                    : ''
                ]"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Satır 3: PID Tanımlayıcıları (M-PID, NS-PID, S-PID) -->
        <b-row v-if="isInternational" class="mt-50">
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group :label-for="`merchant_pid_${index}_${idx}`">
              <template #label>
                <span>{{ t('Merchant Product ID') }}</span>
              </template>
              <b-form-input
                :id="`merchant_pid_${index}_${idx}`"
                v-model="product.merchant_product_id"
                :placeholder="t('e.g. SKU-TSHIRT-001')"
                maxlength="100"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group :label-for="`manufacturer_pid_${index}_${idx}`">
              <template #label>
                <span>{{ t('Manufacturer Product ID') }}</span>
              </template>
              <b-form-input
                :id="`manufacturer_pid_${index}_${idx}`"
                v-model="product.manufacturer_product_id"
                :placeholder="t('e.g. MFR-TS-RED-M')"
                maxlength="100"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group :label-for="`standard_pid_${index}_${idx}`">
              <template #label>
                <span>{{ t('Standard Product ID') }}</span>
              </template>
              <b-form-input
                :id="`standard_pid_${index}_${idx}`"
                v-model="product.standard_product_id"
                :placeholder="t('e.g. 8690000000012')"
                maxlength="100"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Paket Sil Butonu -->
    <b-row v-if="showDeleteButton">
      <b-col cols="12" class="text-right mt-1">
        <b-button
          variant="danger"
          size="sm"
          class="d-inline-flex align-items-center justify-content-center"
          @click="deletePackage"
        >
          <feather-icon icon="XIcon" size="13" />
          <span class="ml-25 align-middle">{{ t('Delete Package') }}</span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import { BButton, BCol, BFormGroup, BFormInput, BFormSelect, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import vSelect from 'vue-select';
import SelectBox from '@/components/SelectBox.vue';
import useService from '@/auth/service/useService';
import axios from '@axios';

export default {
  components: {
    BButton,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    vSelect,
    ValidationProvider,
    SelectBox
  },
  mixins: [helper],
  props: {
    index: {
      type: Number,
      default: 0
    },
    showDeleteButton: {
      type: Boolean,
      default: false
    },
    showDetail: {
      // Eski tasarımla uyumluluk için bırakıldı, şu an kullanılmıyor
      type: Boolean,
      default: false
    },
    showPostType: {
      type: Boolean,
      default: true
    },
    showDangerousGoods: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    isInternational: {
      type: Boolean,
      default: false
    },
    isDomestic: {
      type: Boolean,
      default: false
    },
    consignmentPostType: {
      type: Array,
      default: () => []
    },
    consignmentPackageType: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      validationAttempted: false,
      values: {
        package_type: 1,
        post_type: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        dangerous_goods: null
      },
      products: [
        {
          description: '',
          gtip: '',
          gtipSelect: null,
          origin: null,
          currency: null,
          currency_code: '',
          quantity: '',
          unit_price: '',
          merchant_product_id: '',
          manufacturer_product_id: '',
          standard_product_id: ''
        }
      ],
      productErrors: [],
      submitting: false,
      postTypeList: [],
      packageTypeList: [],
      selectedSavedProduct: '',
      systemProducts: [],
      // Tehlikeli madde alanı için basit bir hata mesajı
      dangerousGoodsError: ''
    };
  },
  computed: {
    ...mapGetters({
      currencies: 'app/currencies',
      countries: 'app/countries'
    }),
    packageTypes() {
      const dynamicPackageTypes = this.packageTypeList.map(item => {
        const image = `/packages/${item.key.toLowerCase()}.png`;
        return {
          type: item.key.toLowerCase(),
          value: item.value,
          image: image || '/packages/default.png',
          title: this.$t(this.capitalizeFirstLetterAndLowercaseRest(item.key))
        };
      });

      return [...dynamicPackageTypes];
    },
    consignmentPostTypeOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.postTypeList && this.postTypeList.length > 0) {
        options.push(
          ...this.postTypeList.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },
    savedProductOptions() {
      const options = [
        {
          text: this.$t('Get From My Saved Products'),
          value: ''
        }
      ];

      if (this.systemProducts && this.systemProducts.length) {
        options.push(
          ...this.systemProducts.map(item => ({
            text: item.description,
            value: item.id
          }))
        );
      }

      return options;
    },
    dangerousGoodsOptions() {
      return [
        { text: this.$t('Make a Choice'), value: null },
        { text: this.$t('Yes'), value: true },
        { text: this.$t('No'), value: false }
      ];
    }
  },
  async created() {
    let postTypes = this.consignmentPostType;
    if (!postTypes || !postTypes.length) {
      postTypes = await this.getConsignmentPostType();
    }
    this.postTypeList = postTypes || [];
    this.$emit('consignment-post-type-loaded', this.postTypeList);

    let packageTypes = this.consignmentPackageType;
    if (!packageTypes || !packageTypes.length) {
      packageTypes = await this.getConsignmentPackageType();
    }
    this.packageTypeList = packageTypes || [];
    this.$emit('consignment-package-type-loaded', this.packageTypeList);

    // Sistem ürünlerini dropdown için yükle
    this.loadSystemProducts();
  },
  mounted() {
    setTimeout(() => {
      Object.entries(this.values).forEach(([key, value]) => {
        if (!value && this.showDetail && !['post_type'].includes(key) && this.$refs[key]) {
          this.$refs[key].reset();
        }
      });
    }, 100);
  },
  methods: {
    async resolveGtipText(index) {
      const product = this.products[index];
      if (!product || !product.gtip) return;
      if (!product.gtipSelect || typeof product.gtipSelect !== 'object') return;

      const id = product.gtipSelect.id || product.gtip;
      const text = (product.gtipSelect.text || '').trim();

      // If text is missing or equals code, try to resolve description via API
      if (!id || id.length < 4) return;
      if (text && text !== id) return;

      try {
        const response = await axios.post('/api/v1/gtip/search', { search: id });
        const first =
          response && response.status && Array.isArray(response.data) ? response.data[0] : null;
        if (!first) return;

        const resolvedText = first.description || first.text || '';
        if (!resolvedText) return;

        // Keep id as code for returnedKey="code" flows
        product.gtipSelect = {
          id: first.code || id,
          text: resolvedText,
          gtipId: first.id
        };
      } catch (_) {
        // Silent: dropdown will still show the code
      }
    },
    loadSystemProducts() {
      const requestData = {
        page: 1,
        limit: 1000
      };

      useService
        .getAllProducts(requestData)
        .then(response => {
          this.systemProducts = Array.isArray(response.data) ? response.data : [];
        })
        .catch(() => {
          this.systemProducts = [];
        });
    },
    createEmptyProduct() {
      return {
        description: '',
        gtip: '',
        // Select2'nin seçili metni gösterebilmesi için obje value
        gtipSelect: null,
        origin: null,
        currency: null,
        currency_code: '',
        quantity: '',
        unit_price: '',
        merchant_product_id: '',
        manufacturer_product_id: '',
        standard_product_id: ''
      };
    },
    translateProductName(index) {
      const product = this.products[index];
      const text = (product.description || '').trim();

      if (!text) {
        this.showErrorMessage(this.$t('Please enter a product name'));
        return;
      }

      const requestData = {
        text,
        targetLanguage: 'en'
      };

      useService
        .getTranslateToEnglish(requestData)
        .then(response => {
          const translatedText = response && response.data ? response.data.translatedText : '';
          if (translatedText) {
            this.products[index].description = translatedText;
          }
          const message =
            (response && response.message) || this.$t('Operation Complete Successfully');
          this.showSuccessMessage(message);
        })
        .catch(error => {
          const message = (error && error.message) || this.$t('An error occurred');
          this.showErrorMessage(message);
        });
    },
    setGtipValue(index, data) {
      const product = this.products[index];
      if (!data) {
        product.gtip = '';
        product.gtipSelect = null;
        return;
      }
      // v-select2-component select event'i obje döndürür: { id, text, ... }
      product.gtip = data.id || '';
      product.gtipSelect = data;
      // Ensure we have description text (sometimes only id is available)
      this.resolveGtipText(index);
    },
    addProductRow() {
      this.products.push(this.createEmptyProduct());
    },
    removeProductRow(index) {
      if (this.products.length === 1) {
        this.$set(this.products, index, this.createEmptyProduct());
        this.selectedSavedProduct = '';
        return;
      }
      this.products.splice(index, 1);
    },
    isEmptyProductRow(product) {
      if (!product) return true;
      return !(
        (product.description && String(product.description).trim()) ||
        (product.gtip && String(product.gtip).trim()) ||
        product.gtipSelect ||
        product.origin ||
        product.currency ||
        (product.currency_code && String(product.currency_code).trim()) ||
        (product.quantity !== '' &&
          product.quantity !== null &&
          typeof product.quantity !== 'undefined') ||
        (product.unit_price !== '' &&
          product.unit_price !== null &&
          typeof product.unit_price !== 'undefined') ||
        (product.merchant_product_id && String(product.merchant_product_id).trim()) ||
        (product.manufacturer_product_id && String(product.manufacturer_product_id).trim()) ||
        (product.standard_product_id && String(product.standard_product_id).trim())
      );
    },
    validateInternationalProducts() {
      // Reset per-row errors for UI hints
      this.productErrors = this.products.map(() => ({}));

      let hasAtLeastOneProduct = false;
      let hasMissingFields = false;

      this.products.forEach((p, idx) => {
        if (this.isEmptyProductRow(p)) return;

        hasAtLeastOneProduct = true;
        const errors = {};

        const desc = (p.description || '').toString().trim();
        const gtip = (p.gtip || '').toString().trim();

        if (!desc) errors.description = true;
        if (!gtip) errors.gtip = true;
        if (!p.origin) errors.origin = true;
        if (!p.currency && !(p.currency_code && String(p.currency_code).trim()))
          errors.currency = true;

        const qty = p.quantity;
        const price = p.unit_price;
        if (qty === '' || qty === null || typeof qty === 'undefined') errors.quantity = true;
        if (price === '' || price === null || typeof price === 'undefined')
          errors.unit_price = true;

        if (Object.keys(errors).length) {
          hasMissingFields = true;
        }

        this.$set(this.productErrors, idx, errors);
      });

      if (!hasAtLeastOneProduct) {
        // No rows filled at all: highlight first row as required so user sees which inputs to fill.
        if (this.products && this.products.length) {
          this.$set(this.productErrors, 0, {
            description: true,
            gtip: true,
            origin: true,
            currency: true,
            quantity: true,
            unit_price: true
          });
        }
        this.showErrorMessage(this.$t('No Package Products Message'));
        return false;
      }

      if (hasMissingFields) {
        this.showErrorMessage(this.$t('Please enter product content'));
        return false;
      }

      return true;
    },
    handleSavedProductChange(value) {
      if (!value) return;

      const selected = this.systemProducts.find(item => item.id === value);
      if (!selected) return;

      // Hedef satır: son satır boş ise onu kullan, değilse yeni satır aç
      let targetIndex = this.products.length - 1;
      if (targetIndex < 0) {
        this.products.push(this.createEmptyProduct());
        targetIndex = 0;
      }

      const last = this.products[targetIndex];
      const isEmptyLast =
        !last.description &&
        !last.gtip &&
        !last.origin &&
        !last.currency &&
        !last.currency_code &&
        !last.quantity &&
        !last.unit_price &&
        !last.merchant_product_id &&
        !last.manufacturer_product_id &&
        !last.standard_product_id;

      if (!isEmptyLast) {
        this.products.push(this.createEmptyProduct());
        targetIndex = this.products.length - 1;
      }

      const currencyCode =
        (selected.currency && selected.currency.code) || selected.currency_code || '';

      const currencyObj =
        this.currencies && this.currencies.length && currencyCode
          ? this.currencies.find(x => x.code === currencyCode) || null
          : null;

      const originCountry = selected.originCountry || {};
      const originObj =
        this.countries && this.countries.length && (originCountry.iso2 || originCountry.iso3)
          ? this.countries.find(
              x =>
                x.iso2 === originCountry.iso2 ||
                x.iso3 === originCountry.iso3 ||
                x.text === originCountry.name ||
                x.value === originCountry.id
            ) || null
          : null;

      this.$set(this.products, targetIndex, {
        description: selected.description || '',
        gtip: selected.gtip && selected.gtip.code ? selected.gtip.code : '',
        gtipSelect:
          selected.gtip && selected.gtip.code
            ? {
                id: selected.gtip.code,
                text: selected.gtip.description || selected.gtip.code,
                gtipId: selected.gtip.id
              }
            : null,
        origin: originObj,
        currency: currencyObj,
        currency_code: currencyCode,
        quantity: '',
        unit_price: '',
        merchant_product_id: selected.merchant_product_id || '',
        manufacturer_product_id: selected.manufacturer_product_id || '',
        standard_product_id: selected.standard_product_id || ''
      });

      // If we only have code, resolve text from API for better UX
      this.$nextTick(() => this.resolveGtipText(targetIndex));
    },
    handleDescriptionInput(index, value) {
      if (!value) {
        if (this.selectedSavedProduct) {
          this.selectedSavedProduct = '';
        }
        this.$set(this.products, index, this.createEmptyProduct());
      }
    },
    setPackageType(value) {
      this.values.package_type = value;
    },
    deletePackage() {
      this.$emit('deletePackage', this.index);
    },
    checkValidation() {
      let valid = true;
      const errorMessages = new Set();

      return new Promise(async resolve => {
        this.validationAttempted = true;
        for (const [key] of Object.entries(this.values)) {
          const shouldValidate =
            key !== 'package_type' &&
            !['dangerous_goods'].includes(key) &&
            (this.showPostType || key !== 'post_type');

          if (shouldValidate) {
            const ref = this.$refs[key];
            if (ref && typeof ref.validate === 'function') {
              const res = await ref.validate();
              if (!res.valid) {
                valid = false;
                if (res.errors && res.errors.length) {
                  res.errors.forEach(e => errorMessages.add(e));
                }
              }
            }
          }
        }

        if (this.isInternational && this.showDangerousGoods) {
          if (this.values.dangerous_goods === null) {
            this.dangerousGoodsError = this.$t('DangerousGoodsRequired');
            valid = false;
            errorMessages.add(this.dangerousGoodsError);
          } else {
            this.dangerousGoodsError = '';
          }
        } else {
          this.dangerousGoodsError = '';
        }

        // International shipments must include at least one complete product row (items must not be empty).
        if (valid && this.isInternational) {
          const productsValid = this.validateInternationalProducts();
          if (!productsValid) {
            valid = false;
          }
        }

        if (!valid && errorMessages.size > 0) {
          errorMessages.forEach(msg => this.showErrorMessage(msg));
        }

        return resolve(valid);
      });
    },
    setData(data) {
      if (!data || typeof data !== 'object') return;

      Object.entries(data).forEach(([key, value]) => {
        if (this.values[key] !== undefined) {
          this.values[key] = value;
        }
      });

      if (Array.isArray(data.items)) {
        if (data.items.length) {
          this.products = data.items.map(item => {
            let cCode = item.currency_code;
            if (!cCode) {
              if (item.currency && typeof item.currency === 'string') {
                cCode = item.currency;
              } else if (item.currency && typeof item.currency === 'object') {
                cCode = item.currency.code;
              }
            }

            const currencyObj =
              this.currencies && this.currencies.length
                ? this.currencies.find(
                    x => x.code === cCode || (cCode === 'TRY' && x.code === 'TL')
                  ) || null
                : null;

            let oIso = item.origin;
            if (item.origin && typeof item.origin === 'object') {
              oIso = item.origin.iso2 || item.origin.value;
            }

            const originObj =
              this.countries && this.countries.length && oIso
                ? this.countries.find(
                    x => x.iso2 === oIso || x.value === oIso || x.text === oIso
                  ) || null
                : null;

            return {
              description: item.description || '',
              gtip: item.gtip || '',
              gtipSelect: item.gtip
                ? {
                    id: item.gtip,
                    text: item.gtip
                  }
                : null,
              origin: originObj,
              currency: currencyObj,
              currency_code: cCode || '',
              quantity: item.quantity !== undefined && item.quantity !== null ? item.quantity : '',
              unit_price:
                item.unit_price !== undefined && item.unit_price !== null ? item.unit_price : '',
              merchant_product_id: item.merchant_product_id || '',
              manufacturer_product_id: item.manufacturer_product_id || '',
              standard_product_id: item.standard_product_id || ''
            };
          });

          // Resolve GTIP description text for loaded data
          this.$nextTick(() => {
            this.products.forEach((p, idx) => {
              if (p && p.gtip) this.resolveGtipText(idx);
            });
          });
        } else {
          this.products = [this.createEmptyProduct()];
        }
      }
    },
    getData(forPrice = false, allFields = false) {
      const data = {};

      Object.entries(this.values).forEach(([key, value]) => {
        if (
          !forPrice ||
          (forPrice &&
            !allFields &&
            !['post_type', 'package_type', 'dangerous_goods'].includes(key)) ||
          (forPrice && allFields)
        ) {
          if (key === 'dangerous_goods') {
            data[key] = value;
          } else if (value !== '' && value !== null && typeof value !== 'undefined') {
            data[key] = parseFloat(value);
          }
        }
      });

      if (this.values.package_type) {
        data.package_type = this.values.package_type;
      }

      // Eski tasarımda quantity alanı kaldırıldığı için 1 gönderiliyor
      data.quantity = 1;

      if (!forPrice) {
        const items = this.products
          .filter(
            p =>
              p.description ||
              p.gtip ||
              p.origin ||
              p.currency ||
              p.currency_code ||
              p.quantity ||
              p.unit_price ||
              p.merchant_product_id ||
              p.manufacturer_product_id ||
              p.standard_product_id
          )
          .map(p => {
            const getNum = v =>
              v !== '' && v !== null && typeof v !== 'undefined' ? parseFloat(v) : undefined;
            const getStr = v =>
              v !== '' && v !== null && typeof v !== 'undefined' && String(v).trim() !== ''
                ? String(v).trim()
                : undefined;

            const item = {
              description: p.description || undefined,
              gtip: p.gtip || undefined,
              origin: (p.origin && (p.origin.iso2 || p.origin.value)) || undefined,
              currency_code: (p.currency && p.currency.code) || undefined,
              quantity: getNum(p.quantity),
              unit_price: getNum(p.unit_price),
              merchant_product_id: getStr(p.merchant_product_id),
              manufacturer_product_id: getStr(p.manufacturer_product_id),
              standard_product_id: getStr(p.standard_product_id)
            };

            Object.keys(item).forEach(key => item[key] === undefined && delete item[key]);
            return item;
          });

        if (items.length) {
          data.items = items;
        }
      }

      return data;
    },
    capitalizeFirstLetterAndLowercaseRest(word) {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style scoped>
.is-invalid :deep(.vs__dropdown-toggle) {
  border-color: #ea5455 !important;
}

.is-invalid :deep(.vs__open-indicator) {
  color: #ea5455 !important;
}

.is-invalid :deep(.select2-container--default .select2-selection--single) {
  border-color: #ea5455 !important;
}

.required-asterisk {
  color: #ea5455;
  margin-left: 4px;
  font-weight: 700;
}

.package-row {
  padding: 15px 15px 10px 15px;
  border: 1px solid #e6e6e6;
  transition-duration: 0.2s;
  border-radius: 5px;
  background: #fafafa;
}

.package-row.package-delete-row {
  padding-bottom: 15px !important;
}

body.dark-layout .package-row {
  border-color: #404656;
  background: #242a3e;
}

.product-section {
  margin-top: 8px;
  padding: 12px;
  border-radius: 5px;
  border: 1px solid #e6e6e6;
  background-color: #fff;
}

body.dark-layout .product-section {
  border-color: #404656;
  background-color: #1f2435;
}

.package-section {
  padding: 12px;
  border-radius: 5px;
  border: 1px solid #e6e6e6;
  background-color: #fff;
}

body.dark-layout .package-section {
  border-color: #404656;
  background-color: #1f2435;
}

.product-section__header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.product-section__bookmark {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: #f3f3f3;
  margin-right: 8px;
}

body.dark-layout .product-section__bookmark {
  background-color: #343b4f;
}

.product-section__add-btn {
  white-space: nowrap;
}

.product-section__row {
  margin-top: 8px;
  padding-top: 8px;
}

.product-section__row + .product-section__row {
  border-top: 1px solid #e6e6e6;
}

body.dark-layout .product-section__row + .product-section__row {
  border-top-color: #404656;
}

.saved-products-select {
  max-width: 620px;
}

.saved-products-select .vs__dropdown-toggle {
  min-height: 30px;
  border-radius: 4px;
  padding-top: 0;
  padding-bottom: 0;
}

.saved-products-select .vs__selected-options {
  font-size: 12px;
}

.saved-products-select .vs__search {
  font-size: 12px;
}

.product-field {
  flex: 0 0 150px;
  max-width: 150px;
}

.product-field--price {
  flex: 0 0 190px;
  max-width: 190px;
}

@media (max-width: 767.98px) {
  .product-field,
  .product-field--price {
    flex: 1 0 50%;
    max-width: 100%;
  }
}
</style>
